import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // undefined = checking, null = unauthenticated, object = authenticated
  const [user, setUser] = useState(undefined);
  // Capture the OAuth-return marker synchronously during render. AuthCallback
  // clears the hash in its own effect (child effects run before ours), so reading
  // window.location.hash inside the effect below would be too late and race checkAuth.
  const isOAuthReturn = useRef(window.location.hash?.includes("session_id=")).current;

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, let AuthCallback establish the session first
    // so its setUser isn't clobbered by a 401 from /auth/me.
    if (isOAuthReturn) return;
    checkAuth();
  }, [checkAuth, isOAuthReturn]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    // Clear user-specific local content so it doesn't linger on a shared device.
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("sh_community_draft_"))
        .forEach((k) => localStorage.removeItem(k));
    } catch { /* noop */ }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
